import {
	Alert,
	Button,
	Group,
	Loader,
	Modal,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
	createReferralReward,
	deleteReferralReward,
	getReferralSettings,
	listReferralRewards,
	referralKeys,
	updateReferralReward,
	updateReferralSettings,
} from "@/features/referral/api/admin-referral";
import type { ReferralProgramValues } from "@/features/referral/lib/referral-schemas";
import { ReferralProgramForm } from "@/features/referral/ui/referral-program-form";
import {
	emptyRewardDraft,
	type RewardDraft,
	RewardEditorForm,
} from "@/features/referral/ui/reward-editor";
import { RewardsTable } from "@/features/referral/ui/rewards-table";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { getMutationErrorMessage } from "@/shared/api/errors";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

export function ReferralSettingsPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const settingsQuery = useQuery({
		queryKey: referralKeys.settings,
		queryFn: getReferralSettings,
	});
	const rewardsQuery = useQuery({
		queryKey: referralKeys.rewards({ limit: 100, page: 1 }),
		queryFn: () => listReferralRewards({ limit: 100, page: 1 }),
	});

	const [editor, setEditor] = useState<RewardDraft | null>(null);
	const [pendingDelete, setPendingDelete] = useState<{
		id: string;
		name: string;
		isActive: boolean;
	} | null>(null);

	const saveMutation = useMutation({
		mutationFn: (values: ReferralProgramValues) =>
			updateReferralSettings({
				isActive: values.isActive,
				pointsPerReferral: Number(values.pointsPerReferral),
				shareText: values.shareText.trim(),
			}),
		onSuccess: async () => {
			notifications.show({ color: "green", message: t("referral.saved") });
			await queryClient.invalidateQueries({ queryKey: referralKeys.settings });
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const rewardMutation = useMutation({
		mutationFn: async (draft: RewardDraft) => {
			const body = {
				reward: draft.reward.trim(),
				pointsCost: draft.pointsCost,
				isActive: draft.isActive,
			};
			if (draft.id) {
				return updateReferralReward(draft.id, body);
			}
			return createReferralReward(body);
		},
		onSuccess: async () => {
			setEditor(null);
			notifications.show({
				color: "green",
				message: t("referral.rewardSaved"),
			});
			await queryClient.invalidateQueries({
				queryKey: referralKeys.rewards({ limit: 100, page: 1 }),
			});
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				message: await getMutationErrorMessage(error),
			});
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteReferralReward(id),
		onSuccess: async () => {
			setPendingDelete(null);
			notifications.show({
				color: "green",
				message: t("referral.rewardDeleted"),
			});
			await queryClient.invalidateQueries({
				queryKey: referralKeys.rewards({ limit: 100, page: 1 }),
			});
		},
		onError: async (error) => {
			notifications.show({
				color: "red",
				message: await getMutationErrorMessage(error),
			});
		},
	});

	return (
		<Stack gap="md">
			<div>
				<Title order={2}>{t("referral.settingsTitle")}</Title>
				<Text c="dimmed" size="sm">
					{t("referral.settingsSubtitle")}
				</Text>
			</div>

			<Paper
				p="md"
				radius="lg"
				withBorder
				w="100%"
				maw={560}
				style={{ minWidth: 0 }}
			>
				{settingsQuery.error ? (
					<Alert mb="md" color="red" title={t("common.loadError")}>
						{describeApiErrorSync(settingsQuery.error)}
					</Alert>
				) : null}
				{settingsQuery.isLoading ? (
					<Loader />
				) : settingsQuery.data ? (
					<ReferralProgramForm
						defaults={{
							isActive: settingsQuery.data.isActive,
							pointsPerReferral: settingsQuery.data.pointsPerReferral,
							shareText: settingsQuery.data.shareText,
						}}
						saving={saveMutation.isPending}
						onSave={(values) => saveMutation.mutate(values)}
					/>
				) : null}
			</Paper>

			<Paper p="md" radius="lg" withBorder style={{ minWidth: 0 }}>
				<Stack>
					<Group
						justify="space-between"
						align="flex-start"
						wrap="wrap"
						gap="sm"
					>
						<Title order={4}>{t("referral.rewardsTitle")}</Title>
						<Button onClick={() => setEditor(emptyRewardDraft())}>
							{t("referral.addReward")}
						</Button>
					</Group>
					{rewardsQuery.error ? (
						<Alert color="red" title={t("common.loadError")}>
							{describeApiErrorSync(rewardsQuery.error)}
						</Alert>
					) : null}
					{rewardsQuery.isLoading ? <Loader /> : null}
					{rewardsQuery.data && rewardsQuery.data.data.length > 0 ? (
						<RewardsTable
							rows={rewardsQuery.data.data}
							onEdit={setEditor}
							onDelete={(row) =>
								setPendingDelete({
									id: row.id,
									name: row.reward,
									isActive: row.isActive,
								})
							}
						/>
					) : null}
				</Stack>
			</Paper>

			<Modal
				opened={Boolean(editor)}
				onClose={() => setEditor(null)}
				title={editor?.id ? t("referral.editReward") : t("referral.addReward")}
				centered
			>
				{editor ? (
					<RewardEditorForm
						key={editor.id ?? "new"}
						draft={editor}
						saving={rewardMutation.isPending}
						onCancel={() => setEditor(null)}
						onSave={(draft) => rewardMutation.mutate(draft)}
					/>
				) : null}
			</Modal>

			<ConfirmDialog
				opened={Boolean(pendingDelete)}
				title={
					pendingDelete?.isActive
						? t("referral.cannotDeleteActiveTitle")
						: t("referral.confirmDeleteTitle")
				}
				body={
					pendingDelete?.isActive
						? t("referral.cannotDeleteActiveBody", {
								name: pendingDelete.name,
							})
						: t("referral.confirmDeleteBody", {
								name: pendingDelete?.name ?? "",
							})
				}
				loading={deleteMutation.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={
					pendingDelete && !pendingDelete.isActive
						? () => deleteMutation.mutate(pendingDelete.id)
						: undefined
				}
			/>
		</Stack>
	);
}
